
import dayjs from "dayjs";
import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";
import {
  DashboardSection,
  DateRange,
  DEFAULT_ENGAGEMENT,
  DEFAULT_SEGMENTS,
  DEFAULT_SUCCESS_CRITERIA,
  EngagementDefinition,
  Segment,
  SuccessCriterion,
} from "../types/analytics";

interface SimplifiedGlobalFilters {
  dateRange: DateRange;
  granularity: "hour" | "day" | "week" | "month";
}

// ============================================================================
// STATE & ACTIONS
// ============================================================================

interface FiltersState {
  filters: SimplifiedGlobalFilters;
  savedSegments: Segment[];
  savedSuccessCriteria: SuccessCriterion[];
  engagementDefinition: EngagementDefinition;
  activeSection: DashboardSection;
  isLoading: boolean;
}

type FiltersAction =
  | { type: "SET_DATE_RANGE"; payload: DateRange }
  | { type: "SET_GRANULARITY"; payload: SimplifiedGlobalFilters["granularity"] }
  | { type: "SAVE_SEGMENT"; payload: Segment }
  | { type: "DELETE_SEGMENT"; payload: string }
  | { type: "SAVE_SUCCESS_CRITERION"; payload: SuccessCriterion }
  | { type: "DELETE_SUCCESS_CRITERION"; payload: string }
  | { type: "SET_SECTION"; payload: DashboardSection }
  | { type: "SET_ENGAGEMENT"; payload: EngagementDefinition }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "RESET_FILTERS" };

// ============================================================================
// INITIAL STATE
// ============================================================================

const getInitialState = (): FiltersState => {
  // Load custom segments from localStorage (only non-built-in ones)
  const customSegmentsJson = localStorage.getItem("analytics_custom_segments");
  const customSegments: Segment[] = customSegmentsJson
    ? JSON.parse(customSegmentsJson)
    : [];
  // Merge: defaults from code + custom from localStorage
  const savedSegments = [...DEFAULT_SEGMENTS, ...customSegments];

  // Load custom success criteria from localStorage (only non-built-in ones)
  const customCriteriaJson = localStorage.getItem(
    "analytics_custom_success_criteria"
  );
  const customCriteria: SuccessCriterion[] = customCriteriaJson
    ? JSON.parse(customCriteriaJson)
    : [];
  // Merge: defaults from code + custom from localStorage
  const savedSuccessCriteria = [...DEFAULT_SUCCESS_CRITERIA, ...customCriteria];

  // Try to load engagement definition
  const engagementJson = localStorage.getItem("analytics_engagement");
  const engagementDefinition = engagementJson
    ? JSON.parse(engagementJson)
    : DEFAULT_ENGAGEMENT;

  return {
    filters: {
      dateRange: {
        start: dayjs().subtract(30, "day").toISOString(),
        end: dayjs().toISOString(),
      },
      granularity: "day",
    },
    savedSegments,
    savedSuccessCriteria,
    engagementDefinition,
    activeSection: "overview",
    isLoading: false,
  };
};

// ============================================================================
// REDUCER
// ============================================================================

function filtersReducer(
  state: FiltersState,
  action: FiltersAction
): FiltersState {
  switch (action.type) {
    case "SET_DATE_RANGE":
      return {
        ...state,
        filters: {
          ...state.filters,
          dateRange: action.payload,
        },
      };

    case "SET_GRANULARITY":
      return {
        ...state,
        filters: {
          ...state.filters,
          granularity: action.payload,
        },
      };

    case "SAVE_SEGMENT": {
      const existingIndex = state.savedSegments.findIndex(
        (s) => s.id === action.payload.id
      );
      const newSegments =
        existingIndex >= 0
          ? state.savedSegments.map((s, i) =>
              i === existingIndex ? action.payload : s
            )
          : [...state.savedSegments, action.payload];

      // Persist only custom (non-built-in) segments to localStorage
      const customSegments = newSegments.filter((s) => !s.isBuiltIn);
      localStorage.setItem(
        "analytics_custom_segments",
        JSON.stringify(customSegments)
      );

      return {
        ...state,
        savedSegments: newSegments,
      };
    }

    case "DELETE_SEGMENT": {
      const newSegments = state.savedSegments.filter(
        (s) => s.id !== action.payload || s.isBuiltIn
      );

      // Persist only custom (non-built-in) segments to localStorage
      const customSegments = newSegments.filter((s) => !s.isBuiltIn);
      localStorage.setItem(
        "analytics_custom_segments",
        JSON.stringify(customSegments)
      );

      return {
        ...state,
        savedSegments: newSegments,
      };
    }

    case "SAVE_SUCCESS_CRITERION": {
      const existingIndex = state.savedSuccessCriteria.findIndex(
        (c) => c.id === action.payload.id
      );
      const newCriteria =
        existingIndex >= 0
          ? state.savedSuccessCriteria.map((c, i) =>
              i === existingIndex ? action.payload : c
            )
          : [...state.savedSuccessCriteria, action.payload];

      // Persist only custom (non-built-in) criteria to localStorage
      const customCriteria = newCriteria.filter((c) => !c.isBuiltIn);
      localStorage.setItem(
        "analytics_custom_success_criteria",
        JSON.stringify(customCriteria)
      );

      return {
        ...state,
        savedSuccessCriteria: newCriteria,
      };
    }

    case "DELETE_SUCCESS_CRITERION": {
      const newCriteria = state.savedSuccessCriteria.filter(
        (c) => c.id !== action.payload || c.isBuiltIn
      );

      // Persist only custom (non-built-in) criteria to localStorage
      const customCriteria = newCriteria.filter((c) => !c.isBuiltIn);
      localStorage.setItem(
        "analytics_custom_success_criteria",
        JSON.stringify(customCriteria)
      );

      return {
        ...state,
        savedSuccessCriteria: newCriteria,
      };
    }

    case "SET_SECTION":
      return {
        ...state,
        activeSection: action.payload,
      };

    case "SET_ENGAGEMENT":
      localStorage.setItem(
        "analytics_engagement",
        JSON.stringify(action.payload)
      );
      return {
        ...state,
        engagementDefinition: action.payload,
      };

    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };

    case "RESET_FILTERS":
      return {
        ...state,
        filters: getInitialState().filters,
      };

    default:
      return state;
  }
}

// ============================================================================
// CONTEXT
// ============================================================================

interface FiltersContextValue extends FiltersState {
  // Actions
  setDateRange: (range: DateRange) => void;
  setGranularity: (granularity: SimplifiedGlobalFilters["granularity"]) => void;
  saveSegment: (segment: Segment) => void;
  deleteSegment: (id: string) => void;
  saveSuccessCriterion: (criterion: SuccessCriterion) => void;
  deleteSuccessCriterion: (id: string) => void;
  setSection: (section: DashboardSection) => void;
  setEngagement: (definition: EngagementDefinition) => void;
  setLoading: (loading: boolean) => void;
  resetFilters: () => void;

  // Quick date range helpers
  setLastNDays: (days: number) => void;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface FiltersProviderProps {
  children: React.ReactNode;
}

export const FiltersProvider: React.FC<FiltersProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(
    filtersReducer,
    undefined,
    getInitialState
  );

  // Memoized action creators
  const setDateRange = useCallback((range: DateRange) => {
    dispatch({ type: "SET_DATE_RANGE", payload: range });
  }, []);

  const setGranularity = useCallback(
    (granularity: SimplifiedGlobalFilters["granularity"]) => {
      dispatch({ type: "SET_GRANULARITY", payload: granularity });
    },
    []
  );

  const saveSegment = useCallback((segment: Segment) => {
    dispatch({ type: "SAVE_SEGMENT", payload: segment });
  }, []);

  const deleteSegment = useCallback((id: string) => {
    dispatch({ type: "DELETE_SEGMENT", payload: id });
  }, []);

  const saveSuccessCriterion = useCallback((criterion: SuccessCriterion) => {
    dispatch({ type: "SAVE_SUCCESS_CRITERION", payload: criterion });
  }, []);

  const deleteSuccessCriterion = useCallback((id: string) => {
    dispatch({ type: "DELETE_SUCCESS_CRITERION", payload: id });
  }, []);

  const setSection = useCallback((section: DashboardSection) => {
    dispatch({ type: "SET_SECTION", payload: section });
  }, []);

  const setEngagement = useCallback((definition: EngagementDefinition) => {
    dispatch({ type: "SET_ENGAGEMENT", payload: definition });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: "RESET_FILTERS" });
  }, []);

  const setLastNDays = useCallback(
    (days: number) => {
      setDateRange({
        start: dayjs().subtract(days, "day").toISOString(),
        end: dayjs().toISOString(),
      });
    },
    [setDateRange]
  );

  const value: FiltersContextValue = {
    ...state,
    setDateRange,
    setGranularity,
    saveSegment,
    deleteSegment,
    saveSuccessCriterion,
    deleteSuccessCriterion,
    setSection,
    setEngagement,
    setLoading,
    resetFilters,
    setLastNDays,
  };

  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export function useFilters(): FiltersContextValue {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error("useFilters must be used within a FiltersProvider");
  }
  return context;
}

export { DEFAULT_SEGMENTS, DEFAULT_SUCCESS_CRITERIA };
